/** Polskie etykiety wartości słownikowych — lustro definicji z backendu. */

import type {
  AttendanceStatus,
  FeedbackKind,
  FeedbackStatus,
  SkillLevel,
  StudentStatus,
  Weekday,
} from '@/api/types'

export const STUDENT_STATUS_LABELS: Record<StudentStatus, string> = {
  ACTIVE: 'Aktywny',
  PAUSED: 'Przerwa',
  ARCHIVED: 'Archiwalny',
}

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  BEGINNER: 'Początkujący',
  INTERMEDIATE: 'Średniozaawansowany',
  ADVANCED: 'Zaawansowany',
}

export const ATTENDANCE_LABELS: Record<AttendanceStatus, string> = {
  PRESENT: 'Obecny',
  ABSENT_NOTIFIED: 'Nieobecny — zgłoszony',
  ABSENT_UNNOTIFIED: 'Nieobecny — bez zgłoszenia',
}

/** Krótkie warianty na przyciski wyboru — pełne nazwy nie mieszczą się na telefonie. */
export const ATTENDANCE_SHORT: Record<AttendanceStatus, string> = {
  PRESENT: 'Obecny',
  ABSENT_NOTIFIED: 'Zgłoszona',
  ABSENT_UNNOTIFIED: 'Bez zgłoszenia',
}

/**
 * Kolejność przycisków na ekranie obecności — od najczęstszego wyboru.
 * Zdecydowana większość zajęć kończy się „obecny”, więc ten przycisk musi być
 * pierwszy.
 *
 * Statusu „spóźniony” nie ma: szkoła uznała go za zbędny, bo nie zmieniał ani
 * rozliczenia z uczniem, ani wynagrodzenia nauczyciela.
 */
export const ATTENDANCE_ORDER: AttendanceStatus[] = [
  'PRESENT',
  'ABSENT_NOTIFIED',
  'ABSENT_UNNOTIFIED',
]

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  MONDAY: 'Poniedziałek',
  TUESDAY: 'Wtorek',
  WEDNESDAY: 'Środa',
  THURSDAY: 'Czwartek',
  FRIDAY: 'Piątek',
  SATURDAY: 'Sobota',
  SUNDAY: 'Niedziela',
}

/**
 * Dzień tygodnia w zdaniu „powtarza się…”.
 *
 * Formy wypisane wprost, bo złożenie „w każdy ” z mianownikiem daje „w każdy
 * sobota”. Środa, sobota i niedziela są rodzaju żeńskiego i wymagają biernika.
 */
export const WEEKDAY_EVERY: Record<Weekday, string> = {
  MONDAY: 'w każdy poniedziałek',
  TUESDAY: 'w każdy wtorek',
  WEDNESDAY: 'w każdą środę',
  THURSDAY: 'w każdy czwartek',
  FRIDAY: 'w każdy piątek',
  SATURDAY: 'w każdą sobotę',
  SUNDAY: 'w każdą niedzielę',
}

export const WEEKDAY_SHORT: Record<Weekday, string> = {
  MONDAY: 'Pn',
  TUESDAY: 'Wt',
  WEDNESDAY: 'Śr',
  THURSDAY: 'Cz',
  FRIDAY: 'Pt',
  SATURDAY: 'So',
  SUNDAY: 'Nd',
}

/** Kolejność dni w polskim kalendarzu — tydzień zaczyna się w poniedziałek. */
export const WEEKDAYS: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
]

/** „14:00:00” → „14:00”. Sekundy są zawsze zerowe i tylko zaśmiecają widok. */
export function shortTime(value: string): string {
  return value.slice(0, 5)
}

/** Wiek na podstawie daty urodzenia — pokazywany na karcie ucznia. */
export function ageFrom(birthDate: string | null): number | null {
  if (!birthDate) return null
  const born = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - born.getFullYear()
  const monthDiff = today.getMonth() - born.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) {
    age -= 1
  }
  return age >= 0 ? age : null
}

export const FEEDBACK_KIND_LABELS: Record<FeedbackKind, string> = {
  BUG: 'Błąd',
  SUGGESTION: 'Sugestia',
}

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  NEW: 'Nowe',
  IN_PROGRESS: 'W toku',
  DONE: 'Zrobione',
  REJECTED: 'Odrzucone',
}
